import React, { useCallback, useMemo } from 'react';
import { Row, Col, Bubble } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import BrandHealthCharts from '@/pages/opinion/components/Dashboard/BrandHealthCharts';
import { useIntl } from 'umi';
import RankCard from '../components/RankCard';
import './index.scss';
import { MentionsSentimentTabType } from '../../constants';
import {
  useKeyMetricsMentionsList,
  useKeyMetricsSentimentList,
} from '../../hooks';
import {
  brandHealthFormat,
  isNilOrEmpty,
  percentageFormat,
  reviewsFormat,
  sentimentFormat,
} from '@op/utils/value';
import { calculateBrandHealthRate } from '@op/utils/text';
import CardCommon from '@op/components/CardCommon';
import { formatTimeToShow, getFilterDateQOQText } from '@op/utils/time';
import BubbleCommon, {
  commonBrandHealthGradeTips,
  commonBrandHealthTips,
  commonSentimentTips,
  mentionsTips,
} from '@op/components/BubbleCommon';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import { isOneDay } from '@op/utils';
import moment from 'moment';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import SmallChart from '../components/SmallChart';

export default function MetricsList({
  tab,
  filter,
  gameData,
  showRank,
  games,
  gameInfo,
}: {
  tab: MentionsSentimentTabType;
  filter: any;
  gameData: any;
  showRank: boolean;
  games: Array<any>;
  gameInfo: any;
}) {
  const { formatMessage } = useIntl();

  const params = useMemo(
    () => ({
      ...filter,
      tab_type: tab,
      games,
      // need_competitor: true,
    }),
    [filter, tab, games],
  );

  const sentimentParams = useMemo(
    () => ({
      ...filter,
      ...gameInfo,
      statistics_field: 'mentions',
    }),
    [filter, gameInfo],
  );

  const { data, loading } = useKeyMetricsMentionsList({
    filter: params,
  });

  const { data: sentimentData, loading: sentimentLoading } =
    useKeyMetricsSentimentList({
      filter: sentimentParams,
    });

  const timeShow = useCallback(
    (timeRange: [string, string]) => {
      const [start_time, end_time] = timeRange;

      if (filter.date_type === 'hourly') {
        return [
          `${moment(start_time).format('YYYY-MM-DD HH:mm')}~${moment(
            end_time,
          ).format('YYYY-MM-DD HH:mm')}`,
        ];
      }
      if (isOneDay(filter)) {
        return [moment(start_time).format('YYYY-MM-DD')];
      }
      return [
        `${moment(start_time).format('YYYY-MM-DD')}~${moment(end_time).format(
          'YYYY-MM-DD',
        )}`,
      ];
    },
    [filter],
  );

  const {
    avg_sentiment,
    avg_sentiment_old,
    avg_sentiment_ratio,
    brand_health,
    brand_health_old,
    brand_health_ratio,
    mentions,
    mentions_old,
    mentions_ratio,
    negative_mentions,
    neutral_mentions,
    positive_mentions,
    rankData = {},
    newTime,
    oldTime,
  } = useMemo(() => {
    return {
      avg_sentiment: data?.avg_sentiment || 0,
      avg_sentiment_old: data?.avg_sentiment_old || 0,
      avg_sentiment_ratio: data?.avg_sentiment_ratio || 0,
      brand_health: data?.brand_health,
      brand_health_old: data?.brand_health_old || null,
      brand_health_ratio: data?.brand_health_ratio || 0,
      mentions: data?.mentions || 0,
      mentions_old: data?.mentions_old || 0,
      mentions_ratio: data?.mentions_ratio || 0,
      negative_mentions: data?.negative_mentions || 0,
      neutral_mentions: data?.neutral_mentions || 0,
      positive_mentions: data?.positive_mentions || 0,
      rankData: data?.rankData || {},
      newTime: data?.newTime || [],
      oldTime: data?.oldTime || [],
    };
  }, [data]);

  const positivePercent = (positive_mentions / mentions) * 100;
  const negativePercent = (negative_mentions / mentions) * 100;
  const neutralPercent = (neutral_mentions / mentions) * 100;

  const formatRankData = useMemo(() => {
    const {
      mentions: mentionsRank = [],
      brand_health: brandHealthRank = [],
      avg_sentiment: avgSentimentRank = [],
    } = rankData;

    const replaceGameKeyWithGameData = (
      rankArray: { game: string; value: number }[],
    ) =>
      rankArray.map((rankItem) => {
        return {
          ...rankItem,
          ...(gameData?.[rankItem.game] || {}),
        };
      });

    return {
      mentions: replaceGameKeyWithGameData(mentionsRank),
      brand_health: replaceGameKeyWithGameData(brandHealthRank),
      avg_sentiment: replaceGameKeyWithGameData(avgSentimentRank),
    };
  }, [rankData, gameData]);

  const brandHealth = calculateBrandHealthRate(brand_health);
  const { text: beforeText } = getFilterDateQOQText(filter);

  function chartTipsElement() {
    return (
      <div className="chart-common-formatter">
        <div className="chart-common-formatter-header">
          <div className="chart-common-formatter-header-left">
            {formatTimeToShow(filter)}
          </div>
          <div className="chart-common-formatter-header-right"></div>
        </div>
        <table className="undefined">
          <tbody>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                {formatMessage({ id: 'Mentions' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(mentions)}
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#69D19C' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Positive' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(positive_mentions)} (
                {percentageFormat(positivePercent)})
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#7E92A1' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Neutral' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(neutral_mentions)} (
                {percentageFormat(neutralPercent)})
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#F17C86' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Negative' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(negative_mentions)} (
                {percentageFormat(negativePercent)})
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <CardCommon contentCard className="metrics-list-wrapper" loading={loading} loadingHeight={149}>
      <Row className="dbo-rank-row">
        <Col span={8}>
          <RankCard
            title={formatMessage({ id: beforeText === 'DoD' ? 'Daily Mentions' : 'Mentions' })}
            rankData={formatRankData.mentions}
            currentGame={gameInfo.edition_unified_id}
            valueFormat={reviewsFormat}
            tips={mentionsTips}
            showRank={showRank}
          >
            <div className="dbo-compare">
              <Bubble
                className="dbo-noborer-wrap"
                placement="right"
                closeOnScroll
                content={chartTipsElement()}
                dark
              >
                <div className="dbo-compare-bubble">
                  <div className="dbo-compare-num">
                    {reviewsFormat(mentions)}
                  </div>
                  <div className="dbo-compare-bar">
                    <div
                      className="dbo-compare-bar-item"
                      style={{
                        width: percentageFormat(positivePercent),
                        backgroundColor: '#69D19C',
                      }}
                    ></div>
                    <div
                      className="dbo-compare-bar-item"
                      style={{
                        width: percentageFormat(neutralPercent),
                        backgroundColor: '#6E7794',
                      }}
                    ></div>
                    <div
                      className="dbo-compare-bar-item"
                      style={{
                        width: percentageFormat(negativePercent),
                        backgroundColor: '#F17C86',
                      }}
                    ></div>
                  </div>
                </div>
              </Bubble>
              <div className="dbo-compare-flex">
                <RingRatioBubble
                  otherInfo={
                    <div className="item-title">
                      {formatMessage({ id: 'Mentions' })}{' '}
                      {formatMessage({ id: beforeText })}
                    </div>
                  }
                  newTime={timeShow(newTime)}
                  oldTime={timeShow(oldTime)}
                  oldValue={brandHealthFormat(mentions_old)}
                  newValue={brandHealthFormat(mentions)}
                  placement="top-start"
                >
                  <>
                    <span className="context-text">
                      {formatMessage({ id: beforeText })}
                    </span>
                    <PercentageItem
                      percentage={mentions_ratio}
                    ></PercentageItem>
                  </>
                </RingRatioBubble>
              </div>
            </div>
          </RankCard>
        </Col>
        <Col span={8}>
          <RankCard
            title={formatMessage({ id: 'Brand Health' })}
            rankData={formatRankData.brand_health}
            currentGame={gameInfo.edition_unified_id}
            valueFormat={brandHealthFormat}
            showRank={showRank}
            tips={commonBrandHealthTips}
          >
            <div className="dbo-compare">
              <div className="dbo-compare-num">
                <BubbleCommon
                  dark
                  placement="top"
                  tipsMap={
                    isNilOrEmpty(brand_health)
                      ? null
                      : commonBrandHealthGradeTips(brandHealth.text)
                  }
                  showIcon={false}
                >
                  <span className="dbo-compare-num-healthy-text">
                    {formatMessage({ id: brandHealth.text })}
                  </span>
                </BubbleCommon>
              </div>
              <div className="dbo-compare-flex">
                <RingRatioBubble
                  otherInfo={
                    <div className="item-title">
                      {formatMessage({ id: 'Brand Health' })}{' '}
                      {formatMessage({ id: beforeText })}
                    </div>
                  }
                  newTime={timeShow(newTime)}
                  oldTime={timeShow(oldTime)}
                  oldValue={brandHealthFormat(brand_health_old)}
                  newValue={brandHealthFormat(brand_health)}
                  placement="top-start"
                >
                  <>
                    <span className="context-text">
                      {formatMessage({ id: beforeText })}
                    </span>
                    <span
                      className={
                        brand_health_ratio > 0
                          ? 'context-success-text'
                          : 'context-red-text'
                      }
                    >
                      {percentageFormat(brand_health_ratio)}
                    </span>
                    <SvgIcon
                      type={
                        brand_health_ratio > 0 ? 'green-up-new' : 'red-down-new'
                      }
                      className="dbo-compare-icon"
                    />
                  </>
                </RingRatioBubble>
              </div>
              {!isNilOrEmpty(brand_health) && (
                <div className="dbo-compare-chart">
                  <BrandHealthCharts
                    brandHealth={Number(brandHealthFormat(brand_health))}
                  />
                  <div className="dbo-compare-chart-num">-100~100</div>
                </div>
              )}
            </div>
          </RankCard>
        </Col>
        <Col span={8}>
          <RankCard
            title={formatMessage({ id: 'Sentiment' })}
            rankData={formatRankData.avg_sentiment}
            currentGame={gameInfo.edition_unified_id}
            valueFormat={sentimentFormat}
            showRank={showRank}
            tips={commonSentimentTips}
          >
            <div className="dbo-compare">
              <div className="dbo-compare-num">
                {sentimentFormat(avg_sentiment)}
                <span className="dbo-compare-num-level">
                  {formatMessage({ id: 'Up to 5' })}
                </span>
              </div>
              <div className="dbo-compare-flex">
                <RingRatioBubble
                  otherInfo={
                    <div className="item-title">
                      {formatMessage({ id: 'Sentiment' })}{' '}
                      {formatMessage({ id: beforeText })}
                    </div>
                  }
                  newTime={timeShow(newTime)}
                  oldTime={timeShow(oldTime)}
                  oldValue={sentimentFormat(avg_sentiment_old)}
                  newValue={sentimentFormat(avg_sentiment)}
                  placement="top-start"
                >
                  <>
                    <span className="context-text">
                      {formatMessage({ id: beforeText })}
                    </span>
                    <span
                      className={
                        avg_sentiment_ratio > 0
                          ? 'context-success-text'
                          : 'context-red-text'
                      }
                    >
                      {percentageFormat(avg_sentiment_ratio)}
                    </span>
                    <SvgIcon
                      type={
                        avg_sentiment_ratio > 0
                          ? 'green-up-new'
                          : 'red-down-new'
                      }
                      className="dbo-compare-icon"
                    />
                  </>
                </RingRatioBubble>
              </div>
              <div className="dbo-compare-chart dbo-compare-small-chart">
                <SmallChart
                  text={''}
                  statistics={{
                    x: sentimentData.xAxisData,
                    y: sentimentData.yAxisData?.map((item: number) =>
                      item === 0 ? null : item,
                    ),
                  }}
                  topicName={formatMessage({ id: 'Sentiment' })}
                  formatValueFunc={(value: number) => sentimentFormat(value)}
                  dateType={filter.date_type}
                />
              </div>
            </div>
          </RankCard>
        </Col>
      </Row>
    </CardCommon>
  );
}
